<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          Issue Report
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex justify-center w-full flex-col space-y-4">
        <h2 class="text-lg font-bold">
          {{ report.title }}
        </h2>

        <div
          v-if="report.issue_to?.length > 0"
          class="mt-4 p-3 bg-blue-50 rounded-md"
        >
          <div class="text-sm font-medium text-blue-900 mb-2">
            This report will be sent to:
          </div>

          <ul class="text-sm text-blue-800 space-y-1">
            <li
              v-for="recipient in report.issue_to"
              :key="recipient.email"
            >
              {{ recipient.user_details?.name || recipient.email }}
              <span
                v-if="recipient.user_details?.name"
                class="text-blue-600"
              >
                ({{ recipient.email }})
              </span>
            </li>
          </ul>
        </div>

        <div
          v-else
          class="mt-4 p-3 bg-yellow-50 rounded-md"
        >
          <div class="text-sm text-yellow-800">
            No recipients configured for this report. Please configure issue recipients in the report settings.
          </div>
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">
            Select Report to Issue
          </label>

          <div class="max-h-96 overflow-y-auto border border-gray-300 rounded-md">
            <div
              v-for="media in availableReports"
              :key="media.uuid"
              class="p-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
              :class="{ 'bg-blue-50 border-blue-200': selectedMedia?.uuid === media.uuid }"
              @click="selectMedia(media)"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div class="font-medium text-gray-900">
                    Report for period {{ transformPeriod(media.custom_properties.period) }}
                  </div>

                  <div class="text-sm text-gray-500">
                    File: {{ media.file_name }}
                  </div>
                </div>

                <div class="flex items-center">
                  <input
                    type="radio"
                    :checked="selectedMedia?.uuid === media.uuid"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    readonly
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <button
          type="button"
          class="btn btn--tertiary-blue"
          @click.prevent="$emit('close')"
        >
          Close
        </button>

        <button
          type="button"
          class="btn btn--primary-blue flex items-center justify-center space-x-2 ml-2"
          :disabled="!selectedMedia || !report.issue_to?.length || issueReportAction.is(States.LOADING)"
          @click="issueReport"
        >
          <svg
    fill="none"
    viewBox="0 0 24 24"
 v-if="issueReportAction.is(States.LOADING)"
           
            class="animate-spin h-5 w-5 text-white">
    <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
    />

    <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
</svg>

          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
           
            class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
</svg>

          <span>
            Issue Report
          </span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { Media } from '@/composables/useMediaCollection';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import Report from '@/models/Report';
import { useReportsStore } from '@/store/reports';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { ref } from 'vue';

interface Props {
  report: Report;
  availableReports: Media[];
}

const props = defineProps<Props>();

const emits = defineEmits<{
  (e: 'close');
}>();

const reportsStore = useReportsStore();
const selectedMedia = ref<Media>();
const issueReportAction = useStoreApiAction(reportsStore.issue);

const selectMedia = (media: Media) => {
  selectedMedia.value = media;
};

const transformPeriod = (date: string) => {
  const start = date.slice(0, 10);
  const end = date.slice(11, 22);

  return `${DateTime.fromFormat(start, 'yyyy-MM-dd').toLocaleString()} to ${
    DateTime.fromFormat(end, 'yyyy-MM-dd').toLocaleString()
  }`;
};

const issueReport = () => {
  if(!selectedMedia.value) return;

  issueReportAction.request(props.report.id, selectedMedia.value.uuid).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Report Issued',
      text: 'The report has been sent to all configured recipients.',
    }).then(() => {
      emits('close');
    });
  }).catch((error) => {
    if(error.status === 404) {
      Swal.fire({
        icon: 'error',
        title: 'Report Not Found',
        text: 'The selected report could not be found.',
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Issue Report',
        text: 'An error occurred while sending the report. Please try again.',
      });
    }
  });
};
</script>

<style scoped></style>
